/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React from 'react';
import { formatCurrency, formatDate, formatDateOnly } from '../../utils/formatters.ts';
import { renderStatusBadge, renderShippingBadge } from '../../utils/renderings.tsx';

interface OrderRowProps {
  order: any;
  orderId: string;
  orderIndex: number;
  totalEarned: number;
  isAdmin: boolean;
  isOrderPaid: (orderId: string) => boolean;
  payoutMade: boolean;
  expandedPayouts: Set<string>;
  expandedOrderItems: Set<string>;
  expandedOrderTotals: Set<string>;
  checkedOrdersSize: number;
  onToggleCheckbox: (orderId: string) => void;
  onTogglePayoutDetails: (orderId: string) => void;
  onToggleOrderItems: (orderId: string) => void;
  onToggleOrderTotals: (orderId: string) => void;
  disableCheckbox?: boolean;
}

const OrderRow: React.FC<OrderRowProps> = ({
  order,
  orderId,
  orderIndex,
  totalEarned,
  isAdmin,
  isOrderPaid,
  payoutMade,
  expandedPayouts,
  expandedOrderItems,
  expandedOrderTotals,
  checkedOrdersSize,
  onToggleCheckbox,
  onTogglePayoutDetails,
  onToggleOrderItems,
  onToggleOrderTotals,
  disableCheckbox = false,
}) => {
    console.log('Order data structure:', {
  orderNumber: order.orderNumber,
  createdAt: order.createdAt,
  createdAtType: typeof order.createdAt,
  allKeys: Object.keys(order)
});
  return (
    <>
      {/* Desktop Row */}
      <div className="grid-row desktop-only">
        <div className="grid-cell order-number-cell" style={{'display':'flex','flexDirection':'column' }}><span>#{order.orderNumber}</span><span style={{'fontSize':'50%' }}>{order.orderId}</span></div>

        <div 
          className={`grid-cell payout-cell ${isAdmin && !disableCheckbox ? 'clickable-cell' : ''} ${!isAdmin && isOrderPaid(orderId) ? 'clickable-cell' : ''} ${isOrderPaid(orderId) ? 'paid' : ''} ${payoutMade ? 'active' : ''} ${expandedPayouts?.has(orderId) ? 'active' : ''}${checkedOrdersSize > 0 ? 'being-paid' : ''} `}
          onClick={() => {
            if (isAdmin && !isOrderPaid(orderId) && !disableCheckbox) {
              onToggleCheckbox(orderId);
            } else if (isOrderPaid(orderId)) {
              onTogglePayoutDetails(orderId);
            }
          }}
          title={disableCheckbox ? 'Refunded orders are not eligible for payout' : undefined}
          style={{ 
            cursor: isAdmin && !isOrderPaid(orderId) ? (disableCheckbox ? 'not-allowed' : 'pointer') : (isOrderPaid(orderId) ? 'pointer' : 'default'),
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            opacity: isOrderPaid(orderId) ? 1 : (disableCheckbox ? 0.4 : 1)
          }}
        >
          {isOrderPaid(orderId) ? (
            <span>✅</span>
          ) : (
            <>
              {isAdmin ? (
                <input 
                  type="checkbox" 
                  checked={payoutMade}
                  disabled={disableCheckbox}
                  readOnly
                  style={{ 
                    cursor: disableCheckbox ? 'not-allowed' : 'pointer',
                    pointerEvents: 'none'
                  }}
                />
              ) : (
                <span style={{ color: 'red', fontSize: '18px' }}>🚫</span>
              )}
            </>
          )}
        </div>
        
        <div className="grid-cell order-date-cell">{formatDateOnly(order.createdAt)}</div>
        <div 
          className={`grid-cell order-count-cell clickable-cell ${expandedOrderItems?.has(orderId) ? 'active' : ''}`}
          onClick={() => onToggleOrderItems(orderId)}
        >
          {order.lineItems?.length || 0}
        </div>
        <div className="grid-cell fulfillment-status-cell">
          <div className="status-badges-container">
            <span className="financial-status">{renderStatusBadge(order.financialStatus, 'financial')}</span>
            <span className="fulfillment-status">{renderStatusBadge(order.fulfillmentStatus, 'fulfillment')}</span>
            {renderShippingBadge(order.printfulShippingInfo, order.orderNumber)}
          </div>
        </div>
        
        <div 
          className={`grid-cell total-profit-cell clickable-cell ${expandedOrderTotals?.has(orderId) ? 'active' : ''}`}
          onClick={() => onToggleOrderTotals(orderId)}
        >
          {formatCurrency(totalEarned)}
        </div>
      </div>

      {/* Mobile Card */}
      <div className="order-card mobile-only">
        <div className="order-card-header">
          <div className="order-number">
            <label>Order:</label>
            <span>#{order.orderNumber}</span>
          </div>
          <div className="order-date">
            <label>Date:</label>
            <span>{formatDate(order.createdAt)}</span>
          </div>
        </div>
        
        <div className="order-card-body">
          <div className="status-section">
            <div className="status-row">
              <label>Financial:</label>
              {renderStatusBadge(order.financialStatus, 'financial')}
            </div>
            <div className="status-row">
              <label>Fulfillment:</label>
              {renderStatusBadge(order.fulfillmentStatus, 'fulfillment')}
            </div>
            {order.printfulShippingInfo && (
              <div className="status-row">
                <label>Shipping:</label>
                {renderShippingBadge(order.printfulShippingInfo, order.orderNumber)}
              </div>
            )}
          </div>
          
          <div className="order-stats">
            <div 
              className={`order-count clickable-cell ${expandedOrderItems?.has(orderId) ? 'active' : ''}`}
              onClick={() => onToggleOrderItems(orderId)}
            >
              <span className="bold">Items:</span>
              <span className="clickable-value">{order.lineItems?.length || 0}</span>
            </div>
            
            <div 
              className={`payout-status ${isAdmin && !disableCheckbox ? 'clickable-cell' : ''} ${!isAdmin && isOrderPaid(orderId) ? 'clickable-cell' : ''} ${isOrderPaid(orderId) ? 'paid' : ''} ${payoutMade ? 'active' : ''} ${expandedPayouts?.has(orderId) ? 'active' : ''}`}
              onClick={() => {
                if (isAdmin && !isOrderPaid(orderId) && !disableCheckbox) {
                  onToggleCheckbox(orderId);
                } else if (isOrderPaid(orderId)) {
                  onTogglePayoutDetails(orderId);
                }
              }}
              title={disableCheckbox ? 'Refunded orders are not eligible for payout' : undefined}
              style={{ 
                cursor: isAdmin && !isOrderPaid(orderId) ? (disableCheckbox ? 'not-allowed' : 'pointer') : (isOrderPaid(orderId) ? 'pointer' : 'default'),
                opacity: isOrderPaid(orderId) ? 1 : (disableCheckbox ? 0.4 : 1)
              }}
            >
              <span className="bold">Profit Paid</span>
              <span className="clickable-value">
                {isOrderPaid(orderId) ? (
                  <span className="checkmark">✓</span>
                ) : (
                  <>
                    {isAdmin ? (
                      <input 
                        type="checkbox" 
                        checked={payoutMade}
                        disabled={disableCheckbox}
                        readOnly
                        style={{ 
                          cursor: disableCheckbox ? 'not-allowed' : 'pointer',
                          pointerEvents: 'none',
                          marginLeft: '5px'
                        }}
                      />
                    ) : (
                      <span style={{ color: 'red', fontSize: '18px' }}>🚫</span>
                    )}
                  </>
                )}
              </span>
            </div>
            
            <div 
              className={`total-profit clickable-cell ${expandedOrderTotals?.has(orderId) ? 'active' : ''}`}
              onClick={() => onToggleOrderTotals(orderId)}
            >
              <span className="bold">Profit:</span>
              <span className="clickable-value">{formatCurrency(totalEarned)}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OrderRow;
